import { getOptimizedMagentoImageUrl, type MagentoImageFormat } from "../getOptimizedMagentoImageUrl";

type SrcSetDescriptor = "density" | "width";

type GetOptimizedSrcSetProps = {
  src?: string;
  baseWidth: number;
  baseHeight?: number;
  quality?: number;
  format?: MagentoImageFormat;
  descriptor?: SrcSetDescriptor;
  widths?: readonly number[];
};

type GetOptimizedSrcSetResult = {
  optimizedImageSrcSet?: string;
  optimizedImageSrc1x?: string;
  optimizedImageSrc2x?: string;
  optimizedImageSrc3x?: string;
};

const DENSITY_FACTORS = [1, 2, 3] as const;

const getScaledHeight = ({
  width,
  baseWidth,
  baseHeight,
}: {
  width: number;
  baseWidth: number;
  baseHeight?: number;
}): number | undefined => {
  if (!baseHeight) {
    return undefined;
  }

  return Math.round((width / baseWidth) * baseHeight);
};

const getOptimizedSrc = ({
  src,
  width,
  height,
  quality,
  format,
}: {
  src: string;
  width: number;
  height?: number;
  quality: number;
  format?: MagentoImageFormat;
}) =>
  getOptimizedMagentoImageUrl({
    url: src,
    width,
    height,
    quality,
    format,
  });

export function getOptimizedMagentoImageSrcSet(props: GetOptimizedSrcSetProps): GetOptimizedSrcSetResult {
  const { src, baseWidth, baseHeight, quality = 80, format, descriptor = "density", widths } = props;

  if (!src) {
    return {};
  }

  // Width descriptors are opt-in so existing callers keep the legacy density
  // srcset behavior unless they explicitly render a responsive <picture>.
  if (descriptor === "width") {
    const resolvedWidths = widths?.length ? widths : [baseWidth];
    const optimizedSources = resolvedWidths.map((width) => {
      const height = getScaledHeight({ width, baseWidth, baseHeight });
      const optimizedUrl = getOptimizedSrc({ src, width, height, quality, format });

      return { optimizedUrl, width };
    });
    const optimizedImageSrcSet = optimizedSources
      .map(({ optimizedUrl, width }) => `${optimizedUrl} ${width}w`)
      .join(", ");

    return {
      optimizedImageSrcSet,
      optimizedImageSrc1x: optimizedSources[0]?.optimizedUrl,
    };
  }

  // Preserve the original 1x/2x/3x output for current consumers such as cards
  // that are already tuned around device-pixel-ratio selection.
  const [optimizedImageSrc1x, optimizedImageSrc2x, optimizedImageSrc3x] = DENSITY_FACTORS.map((factor) => {
    const width = baseWidth * factor;
    const height = baseHeight ? baseHeight * factor : undefined;

    return getOptimizedSrc({ src, width, height, quality, format });
  });

  const optimizedImageSrcSet = `${optimizedImageSrc1x} 1x, ${optimizedImageSrc2x} 2x, ${optimizedImageSrc3x} 3x`;

  return { optimizedImageSrcSet, optimizedImageSrc1x, optimizedImageSrc2x, optimizedImageSrc3x };
}
